import { ErrorToastType } from '../../../components/popup/types';
import { BetStatusWatcher, BetStatusWatcherStates } from '../../../logger/BetStatusWatcher';
import {
  BetAcceptedEventBitMask,
  BetAcceptedEventBitMaskTypes,
} from '../../../logger/bitMasks/BetAcceptedEventBitMask';
import { Logger } from '../../../logger/Logger';
import { autoplayActions } from '../../../store/slices/autoplay/slice';
import { betActions } from '../../../store/slices/bets/slice';
import type { AcceptedBet } from '../../../store/slices/bets/types';
import { ChipTypes } from '../../../store/slices/chips/types';
import { gameStateActions } from '../../../store/slices/game/slice';
import { GameStatus } from '../../../store/slices/game/types';
import { toastActions } from '../../../store/slices/toast/slice';
import type { AcceptanceState } from '../types';

import { BaseCoreGameCommunicationProcessor } from './BaseCoreGameCommunicationProcessor';

export abstract class BaseBetGameCommunicationProcessor extends BaseCoreGameCommunicationProcessor {
  public updateGameStatus(): void {
    const state = this.store.getState();
    const checkResult = this.checkThatAllBetsAreProcessed(state.bets.acceptedBets);
    const isAutoplay = state.autoplay.isInProgress;
    const isFreeBet = state.bets.acceptedBets.some((bet) => bet.type === ChipTypes.FreeChip);
    const subtitlesEnabled = state.subtitles.isEnabled;

    if (isAutoplay) {
      BetAcceptedEventBitMask.apply(BetAcceptedEventBitMaskTypes.AutplayOn);
    }
    if (isFreeBet) {
      BetAcceptedEventBitMask.apply(BetAcceptedEventBitMaskTypes.FreebetsOn);
    }
    if (subtitlesEnabled) {
      BetAcceptedEventBitMask.apply(BetAcceptedEventBitMaskTypes.SubtitlesOn);
    }

    if (checkResult === 'accepted') {
      this.store.dispatch(gameStateActions.setCurrentGameStatus(GameStatus.BetsAccepted));
      BetStatusWatcher.setState(BetStatusWatcherStates.AllAccepted);
      Logger.logEvent('betsAccepted', { additional: 'all', bandwidth: BetAcceptedEventBitMask.maskedBinary });
    } else if (checkResult === 'not_accepted') {
      BetStatusWatcher.setState(BetStatusWatcherStates.SomeAccepted);
      this.store.dispatch(toastActions.createToast(ErrorToastType.NOT_ALL_BETS_ACCEPTED));
      this.store.dispatch(gameStateActions.setCurrentGameStatus(GameStatus.NotAllBetsAccepted));
      Logger.logEvent('betsAccepted', { additional: 'some', bandwidth: BetAcceptedEventBitMask.maskedBinary });
    } else if (checkResult === 'rejected') {
      this.store.dispatch(toastActions.createToast(ErrorToastType.BETS_REJECTED));
      this.store.dispatch(gameStateActions.setCurrentGameStatus(GameStatus.BetsRejected));
      // If autoplay is in progress, stop it and notify the user
      if (isAutoplay) {
        this.store.dispatch(autoplayActions.stop());
        this.store.dispatch(toastActions.createToast('AUTOPLAY_STOPPED'));
      }
      this.store.dispatch(betActions.resetBetStack());
      BetStatusWatcher.setState(BetStatusWatcherStates.NoneAccepted);
      Logger.logEvent('betsAccepted', { additional: 'none', bandwidth: BetAcceptedEventBitMask.maskedBinary });
    }

    BetAcceptedEventBitMask.reset();
  }

  private checkThatAllBetsAreProcessed(acceptedBets: AcceptedBet[]): AcceptanceState {
    let rejectedBetsCount = 0;

    for (const bet of acceptedBets) {
      // error code -1 is set for bets that are yet to receive their resolve status
      if (bet.errorCode === -1) {
        return 'unknown';
      } else if (bet.errorCode > 0) {
        rejectedBetsCount++;
      }
    }

    if (rejectedBetsCount === acceptedBets.length) {
      return 'rejected';
    } else if (rejectedBetsCount > 0) {
      return 'not_accepted';
    }

    return 'accepted';
  }
}
